import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { SnackbarService } from 'ok-angular-shared/components';
import { catchError, distinctUntilChanged, filter, map, of, switchMap } from 'rxjs';
import { AccessGroupService } from '../../services/access-group.service';
import { AdmG4CustomerNumberComponent } from '../adm-g4-customer-number/adm-g4-customer-number.component';

@Component({
  selector: 'adm-access-group-customer-number-management',
  imports: [ReactiveFormsModule, MatIconModule, MatButtonModule, AdmG4CustomerNumberComponent],
  templateUrl: './access-group-customer-number-management.component.html',
  styleUrl: './access-group-customer-number-management.component.scss',
})
export class AccessGroupCustomerNumberManagementComponent {
  private snackbarService = inject(SnackbarService);
  private accessGroupService = inject(AccessGroupService);

  public accessGroupIdentifier = input.required<string>();
  public saving = input(false);
  public saveCustomerNumbers = output<number[]>();
  public customerNumbersChanged = output<number[]>();

  public initialCustomerNumbers = signal<number[]>([]);
  public customerNumbers = signal<number[]>([]);
  public editingCustomerNumber = signal<number | null>(null);
  public customerNumberControl = new FormControl<string | null>(null, [Validators.required]);
  public formGroup = new FormGroup({
    customerNumber: this.customerNumberControl,
  });

  // Validate that list is actually changed before enabling save button.
  public hasChanges = computed(() => {
    const initial = [...this.initialCustomerNumbers()].sort((a, b) => a - b);
    const current = [...this.customerNumbers()].sort((a, b) => a - b);

    if (initial.length !== current.length) {
      return true;
    }

    return initial.some((value, index) => value !== current[index]);
  });

  constructor() {
    effect(
      () => {
        const initial = this.initialCustomerNumbers();
        this.customerNumbers.set([...initial]);
        this.editingCustomerNumber.set(null);
        this.clearCustomerNumberInput();
      },
      { allowSignalWrites: true }
    );

    toObservable(this.accessGroupIdentifier)
      .pipe(
        filter((identifier): identifier is string => !!identifier),
        distinctUntilChanged(),
        switchMap(identifier =>
          this.accessGroupService.getAccessGroup(identifier).pipe(
            map(accessGroup => accessGroup.customerNumbers ?? []),
            catchError(() => {
              this.snackbarService.error('Fejl ved indlæsning af kundenumre.');
              return of([]);
            })
          )
        ),
        takeUntilDestroyed()
      )
      .subscribe(customerNumbers => {
        this.initialCustomerNumbers.set([...customerNumbers]);
      });
  }

  public addCustomerNumber() {
    this.customerNumberControl.markAsTouched();

    if (this.customerNumberControl.invalid || this.customerNumberControl.pending) {
      return;
    }

    const value = this.customerNumberControl.value?.trim();
    if (!value) {
      return;
    }

    const customerNumber = Number(value);

    if (!Number.isInteger(customerNumber)) {
      this.snackbarService.error('Kundenummeret er ugyldigt.');
      return;
    }

    const editingValue = this.editingCustomerNumber();

    if (editingValue !== null) {
      const nextNumbers = [...this.customerNumbers()];
      const editIndex = nextNumbers.findIndex(number => number === editingValue);
      const duplicateExists = nextNumbers.some(number => number === customerNumber && number !== editingValue);

      if (duplicateExists) {
        this.snackbarService.error('Kundenummeret er allerede tilføjet.');
        return;
      }

      if (editIndex === -1) {
        this.snackbarService.error('Kundenummeret kunne ikke redigeres.');
        return;
      }

      nextNumbers[editIndex] = customerNumber;
      this.customerNumbers.set(nextNumbers);
      this.cancelEditCustomerNumber();
      this.emitCustomerNumbersChanged();

      return;
    }
    if (this.customerNumbers().includes(customerNumber)) {
      this.snackbarService.error('Kundenummeret er allerede tilføjet.');
      return;
    }

    this.customerNumbers.update(numbers => [...numbers, customerNumber]);
    this.clearCustomerNumberInput();
    this.emitCustomerNumbersChanged();
  }

  public startEditCustomerNumber(customerNumber: number) {
    this.editingCustomerNumber.set(customerNumber);
    this.customerNumberControl.setValue(customerNumber.toString());
    this.customerNumberControl.markAsTouched();
    this.customerNumberControl.updateValueAndValidity();
  }

  public cancelEditCustomerNumber() {
    this.editingCustomerNumber.set(null);
    this.clearCustomerNumberInput();
  }

  public removeCustomerNumber(customerNumber: number) {
    this.customerNumbers.update(numbers => numbers.filter(number => number !== customerNumber));
    this.emitCustomerNumbersChanged();
  }

  public emitSaveCustomerNumbers() {
    this.saveCustomerNumbers.emit([...this.customerNumbers()]);
  }

  private clearCustomerNumberInput() {
    this.customerNumberControl.setValue(null);
    this.customerNumberControl.markAsUntouched();
  }

  public isAddCustomerNumberBlocked() {
    return (
      this.saving() || !this.customerNumberControl.value?.trim() || this.customerNumberControl.invalid || this.customerNumberControl.pending
    );
  }

  private emitCustomerNumbersChanged() {
    this.customerNumbersChanged.emit([...this.customerNumbers()]);
  }
}
